import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaCheckCircle, FaSyncAlt } from "react-icons/fa";
import { CartContext } from "../context/CartContext";
import { getPaymentStatus } from "../services/paymentService";
import { clearMonobankPaymentDraft } from "../utils/monobankPaymentDraft";
import styles from "./PaymentStatus.module.css";

const SUCCESS_PAYMENT_STATUSES = new Set([
  "paid",
  "success",
  "hold",
  "authorized",
  "blocked",
  "reserved",
]);
const SUCCESS_AUTH_STATUSES = new Set([
  "held",
  "hold",
  "authorized",
  "blocked",
  "reserved",
]);

const normalizePaymentStatus = (value) =>
  String(value || "").trim().toLowerCase();

const isSuccessfulPaymentStatus = (paymentStatus, holdStatus) =>
  SUCCESS_PAYMENT_STATUSES.has(normalizePaymentStatus(paymentStatus)) ||
  SUCCESS_AUTH_STATUSES.has(normalizePaymentStatus(holdStatus));

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useContext(CartContext);
  const orderId =
    searchParams.get("orderId") || sessionStorage.getItem("monobankPendingOrderId");

  const [status, setStatus] = useState(null);
  const [isChecking, setIsChecking] = useState(true);
  const [countdown, setCountdown] = useState(10);
  const verifyStartedRef = useRef(false);
  const navigationStartedRef = useRef(false);

  const navigateHome = useCallback(() => {
    if (navigationStartedRef.current) return;

    navigationStartedRef.current = true;
    navigate("/");
  }, [navigate]);

  useEffect(() => {
    if (verifyStartedRef.current) return undefined;
    verifyStartedRef.current = true;

    const verifyPaidStatus = async () => {
      if (!orderId) {
        setIsChecking(false);
        return;
      }

      try {
        const data = await getPaymentStatus(orderId);

        setStatus(data);

        if (isSuccessfulPaymentStatus(data.paymentStatus, data.holdStatus)) {
          localStorage.removeItem("checkoutData");
          clearMonobankPaymentDraft();
          clearCart();
        }
      } catch (error) {
        console.error("❌ Не вдалося підтвердити оплату:", error);
      } finally {
        setIsChecking(false);
      }
    };

    verifyPaidStatus();

    return undefined;
  }, [clearCart, orderId]);

  useEffect(() => {
    if (
      !isSuccessfulPaymentStatus(status?.paymentStatus, status?.holdStatus)
    ) {
      return undefined;
    }

    setCountdown(10);

    const intervalId = window.setInterval(() => {
      setCountdown((current) => {
        if (current <= 1) {
          window.clearInterval(intervalId);
          navigateHome();
          return 0;
        }

        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [navigateHome, status?.holdStatus, status?.paymentStatus]);

  if (isChecking) {
    return (
      <div className={styles.statusPage}>
        <div className={styles.statusPanel}>
          <FaSyncAlt className={styles.icon} />
          <h1 className={styles.title}>Перевіряємо оплату</h1>
          <p className={styles.text}>Будь ласка, зачекайте.</p>
        </div>
      </div>
    );
  }

  const isPaymentConfirmed = isSuccessfulPaymentStatus(
    status?.paymentStatus,
    status?.holdStatus
  );
  const orderNumberText = status?.orderNumber ? ` № ${status.orderNumber}` : "";

  if (!isPaymentConfirmed) {
    return (
      <div className={styles.statusPage}>
        <div className={styles.statusPanel}>
          <FaSyncAlt className={styles.icon} />
          <h1 className={styles.title}>Оплату ще не підтверджено</h1>
          {/* <p className={styles.text}>
            Ми не показуємо успішну оплату без підтвердження від backend.
          </p> */}
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.button}
              onClick={() => navigate(`/payment/processing?orderId=${orderId}`)}
            >
              Перевірити ще раз
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.statusPage}>
      <div className={styles.statusPanel}>
        <FaCheckCircle className={`${styles.icon} ${styles.successIcon}`} />
        <h1 className={styles.title}>Оплату підтверджено</h1>
        <p className={styles.text}>
          {`Оплату успішно проведено. Дякуємо за замовлення${orderNumberText}!`}
        </p>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.button}
            onClick={navigateHome}
          >
            Повернутися на головну сторінку
          </button>
        </div>
        <p className={styles.countdownText}>
          Автоматичне повернення на головну сторінку відбудеться через{" "}
          {countdown} сек.
        </p>
      </div>
    </div>
  );
};

export default PaymentSuccess;
